import React, { useState, useMemo } from "react";
import { Card, Button, message, Row, Col, Statistic, Input, Select, Typography, Table, Tag, Space, Modal, Descriptions, Divider, Dropdown } from "antd";
import { FileTextOutlined, DownloadOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, DollarOutlined, PrinterOutlined, DownOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import * as accountSettingsService from "../../services/accountSettingsService";
import * as jobService from "../Jobs/services/jobService";
import { useAuth } from "../../AuthContext";
import { generateInvoicePDF } from "../Reports/services/invoiceGenerator";
import { getAppliedDiscount } from "../../utils/pricingTiers";
import ReceiptPreview from "../../components/ReceiptPreview";
import dayjs from "dayjs";

const { Option } = Select;
const { Text } = Typography;

const ContractsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [receiptInvoice, setReceiptInvoice] = useState(null);

  const { token } = useAuth();

  // Fetch account settings
  const { data: accountSettings } = useQuery({
    queryKey: ["accountSettings", token],
    queryFn: () => accountSettingsService.getAccountSettings(),
    enabled: !!token,
  });

  // Fetch wholesale orders (Jobs) as invoices
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ["jobs", "invoices", token],
    queryFn: () => jobService.getJobsList({ pageSize: 1000, searchTerm: "", statusFilter: "All" }),
    enabled: !!token,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const jobs = jobsData?.jobs || [];

  // Calculate invoice data from job
  const calculateInvoiceData = (job) => {
    // Calculate product cost from job.sales
    let productCost = 0;
    let totalQuantity = 0;
    const products = [];

    if (job.sales && Array.isArray(job.sales)) {
      job.sales.forEach((sale) => {
        if (sale.saleItems && Array.isArray(sale.saleItems)) {
          sale.saleItems.forEach((saleItem) => {
            const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
            const quantity = parseInt(saleItem.quantity) || 1;
            const lineTotal = unitPrice * quantity;
            productCost += lineTotal;
            totalQuantity += quantity;
            products.push({
              name: saleItem.product?.name || 'Unknown Product',
              quantity,
              unitPrice,
              lineTotal
            });
          });
        }
      });
    }

    // Get discount from job override or global tiers
    const appliedDiscount = getAppliedDiscount(job, accountSettings, totalQuantity);
    const discountPercent = appliedDiscount.discount;
    const discountLabel = appliedDiscount.label ? `${appliedDiscount.label} (${discountPercent}%)` : "";

    const discountAmount = productCost * (discountPercent / 100);
    const subtotalAfterDiscount = productCost - discountAmount;
    const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
    const taxAmount = job.includeTax ? subtotalAfterDiscount * taxRate : 0;
    const totalCost = subtotalAfterDiscount + taxAmount;

    // Calculate payments
    const totalPaid = Array.isArray(job.payments) && job.payments.length > 0
      ? job.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
      : 0;

    const balanceDue = totalCost - totalPaid;
    let paymentStatus = "UNPAID";
    if (totalPaid >= totalCost && totalCost > 0) {
      paymentStatus = "PAID";
    } else if (totalPaid > 0) {
      paymentStatus = "PARTIAL";
    }

    return {
      productCost,
      totalQuantity,
      discountPercent,
      discountLabel,
      discountAmount,
      subtotalAfterDiscount,
      taxAmount,
      totalCost,
      totalPaid,
      balanceDue,
      paymentStatus,
      products,
    };
  };

  // Transform jobs into invoice format
  const invoices = useMemo(() => {
    return jobs.map((job) => {
      const invoiceData = calculateInvoiceData(job);
      return {
        id: job.id,
        job: job,
        customerName: job.customer?.name || job.customerName || "Unknown Customer",
        customerAddress: [
          job.customer?.addressLine1,
          job.customer?.city,
          job.customer?.state,
          job.customer?.zip
        ].filter(Boolean).join(", "),
        customerPhone: job.customer?.phone,
        customerEmail: job.customer?.username,
        date: job.startDate || job.createdAt,
        title: job.title,
        ...invoiceData,
        paymentTerms: job.customer?.paymentTerms || "DUE_ON_RECEIPT",
      };
    });
  }, [jobs]);

  // Filter invoices based on search and status
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const matchesSearch =
        !searchTerm ||
        invoice.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(invoice.id).includes(searchTerm);

      const matchesStatus =
        statusFilter === "All" || invoice.paymentStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = invoices.length;
    const paid = invoices.filter((i) => i.paymentStatus === "PAID").length;
    const unpaid = invoices.filter((i) => i.paymentStatus === "UNPAID").length;
    const partial = invoices.filter((i) => i.paymentStatus === "PARTIAL").length;
    const totalValue = invoices.reduce((sum, i) => sum + (i.totalCost || 0), 0);
    const totalOutstanding = invoices.reduce((sum, i) => sum + (i.balanceDue > 0 ? i.balanceDue : 0), 0);
    return { total, paid, unpaid, partial, totalValue, totalOutstanding };
  }, [invoices]);

  // Handle PDF download
  const handleDownloadPDF = async (invoice) => {
    try {
      message.loading({ content: "Generating PDF...", key: "pdf" });
      await generateInvoicePDF(invoice.job, accountSettings);
      message.success({ content: "PDF downloaded successfully!", key: "pdf" });
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error({ content: "Failed to generate PDF", key: "pdf" });
    }
  };

  // Handle receipt preview
  const handleOpenReceipt = (invoice) => {
    setReceiptInvoice(invoice);
  };

  // Download dropdown menu items
  const getDownloadMenuItems = (record) => [
    {
      key: 'pdf',
      label: 'Standard Invoice (PDF)',
      icon: <DownloadOutlined />,
      onClick: () => handleDownloadPDF(record),
    },
    {
      key: 'receipt',
      label: 'Thermal Receipt (58mm)',
      icon: <PrinterOutlined />,
      onClick: () => handleOpenReceipt(record),
    },
  ];

  const getPaymentTermsLabel = (terms) => {
    switch (terms) {
      case "NET_15": return "Net 15 Days";
      case "NET_30": return "Net 30 Days";
      case "NET_60": return "Net 60 Days";
      default: return "Due on Receipt";
    }
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "id",
      key: "id",
      width: 90,
      render: (id) => <Text strong>INV-{id}</Text>,
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: "descend",
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, record) => (
        <div>
          <Text strong>{record.customerName}</Text>
          {record.title && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{record.title}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => date ? dayjs(date).format("MMM D, YYYY") : "-",
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    {
      title: "Amount",
      key: "amount",
      width: 120,
      render: (_, record) => (
        <div>
          <Text strong style={{ color: "#3f8600" }}>
            ${record.totalCost.toFixed(2)}
          </Text>
          {record.discountLabel && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>{record.discountLabel}</Text>
            </div>
          )}
        </div>
      ),
      sorter: (a, b) => a.totalCost - b.totalCost,
    },
    {
      title: "Balance",
      key: "balance",
      width: 100,
      render: (_, record) => (
        <Text style={{ color: record.balanceDue > 0 ? "#cf1322" : "#3f8600" }}>
          ${record.balanceDue.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.balanceDue - b.balanceDue,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) => (
        <Tag color={record.paymentStatus === "PAID" ? "success" : record.paymentStatus === "PARTIAL" ? "warning" : "error"}>
          {record.paymentStatus}
        </Tag>
      ),
      filters: [
        { text: "Paid", value: "PAID" },
        { text: "Unpaid", value: "UNPAID" },
        { text: "Partial", value: "PARTIAL" },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setSelectedInvoice(record)}
          />
          <Dropdown
            menu={{ items: getDownloadMenuItems(record) }}
            trigger={['click']}
          >
            <Button type="text" icon={<DownloadOutlined />}>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full h-full" style={{ padding: "16px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Invoices
          </Typography.Title>
          <Text type="secondary">View and download wholesale order invoices</Text>
        </div>
      </div>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Total Invoices"
              value={stats.total}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Paid"
              value={stats.paid}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Unpaid / Partial"
              value={stats.unpaid + stats.partial}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Outstanding"
              value={stats.totalOutstanding}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: stats.totalOutstanding > 0 ? "#cf1322" : "#3f8600" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={24} md={12}>
            <Input
              size="large"
              placeholder="Search by customer, order title, or invoice #..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Select
              size="large"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: "100%" }}
            >
              <Option value="All">All Statuses</Option>
              <Option value="PAID">Paid</Option>
              <Option value="UNPAID">Unpaid</Option>
              <Option value="PARTIAL">Partial</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Invoices Table - Desktop */}
      <div className="hidden md:block">
        <Card size="small">
          <Table
            columns={columns}
            dataSource={filteredInvoices}
            rowKey="id"
            loading={jobsLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${total} invoices`,
            }}
          />
        </Card>
      </div>

      {/* Invoices Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {jobsLoading ? (
          <Card size="small" loading={true} />
        ) : filteredInvoices.length > 0 ? (
          filteredInvoices.map((invoice) => (
            <Card key={invoice.id} size="small" className="shadow-sm">
              {/* Header with Invoice # and Status */}
              <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100">
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Invoice</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>INV-{invoice.id}</Text>
                  </div>
                </div>
                <Tag
                  color={invoice.paymentStatus === "PAID" ? "success" : invoice.paymentStatus === "PARTIAL" ? "warning" : "error"}
                >
                  {invoice.paymentStatus}
                </Tag>
              </div>

              {/* Customer Info */}
              <div className="mb-3">
                <Text strong>{invoice.customerName}</Text>
                {invoice.title && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{invoice.title}</Text>
                  </div>
                )}
              </div>

              {/* Date */}
              <div className="mb-3">
                <Text type="secondary" style={{ fontSize: 11 }}>Date: </Text>
                <Text style={{ fontSize: 13 }}>
                  {invoice.date ? dayjs(invoice.date).format("MMM D, YYYY") : "-"}
                </Text>
              </div>

              {/* Amount & Balance Row */}
              <Row gutter={16} className="mb-3">
                <Col span={12}>
                  <div className="text-xs text-gray-500">Total Amount</div>
                  <div className="text-base font-semibold text-green-600">
                    ${invoice.totalCost.toFixed(2)}
                  </div>
                  {invoice.discountLabel && (
                    <Text type="secondary" style={{ fontSize: 10 }}>{invoice.discountLabel}</Text>
                  )}
                </Col>
                <Col span={12}>
                  <div className="text-xs text-gray-500">Balance Due</div>
                  <div
                    className="text-base font-semibold"
                    style={{ color: invoice.balanceDue > 0 ? "#cf1322" : "#3f8600" }}
                  >
                    ${invoice.balanceDue.toFixed(2)}
                  </div>
                </Col>
              </Row>

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => setSelectedInvoice(invoice)}
                  className="flex-1"
                >
                  View Details
                </Button>
                <Dropdown
                  menu={{ items: getDownloadMenuItems(invoice) }}
                  trigger={['click']}
                >
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    className="flex-1"
                  >
                    Download <DownOutlined style={{ fontSize: 10 }} />
                  </Button>
                </Dropdown>
              </div>
            </Card>
          ))
        ) : (
          <Card size="small">
            <div className="text-center py-8 text-gray-500">No invoices found</div>
          </Card>
        )}
      </div>

      {/* Invoice Detail Modal */}
      <Modal
        title={`Invoice #INV-${selectedInvoice?.id}`}
        open={!!selectedInvoice}
        onCancel={() => setSelectedInvoice(null)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setSelectedInvoice(null)}>
            Close
          </Button>,
          <Dropdown
            key="download"
            menu={{
              items: [
                {
                  key: 'pdf',
                  label: 'Standard Invoice (PDF)',
                  icon: <DownloadOutlined />,
                  onClick: () => {
                    handleDownloadPDF(selectedInvoice);
                    setSelectedInvoice(null);
                  },
                },
                {
                  key: 'receipt',
                  label: 'Thermal Receipt (58mm)',
                  icon: <PrinterOutlined />,
                  onClick: () => {
                    handleOpenReceipt(selectedInvoice);
                    setSelectedInvoice(null);
                  },
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="primary" icon={<DownloadOutlined />}>
              Download <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>,
        ]}
      >
        {selectedInvoice && (
          <>
            <Descriptions title="Customer" bordered size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Name">{selectedInvoice.customerName || "-"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedInvoice.customerPhone || "-"}</Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>{selectedInvoice.customerEmail || "-"}</Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>{selectedInvoice.customerAddress || "-"}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="Order Details" bordered size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Order">{selectedInvoice.title || "-"}</Descriptions.Item>
              <Descriptions.Item label="Date">
                {selectedInvoice.date ? dayjs(selectedInvoice.date).format("MMM D, YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Items">{selectedInvoice.totalQuantity} items</Descriptions.Item>
              <Descriptions.Item label="Payment Terms">
                {getPaymentTermsLabel(selectedInvoice.paymentTerms)}
              </Descriptions.Item>
            </Descriptions>

            {/* Products List */}
            {selectedInvoice.products && selectedInvoice.products.length > 0 && (
              <>
                <Divider orientation="left" style={{ marginTop: 16, marginBottom: 8 }}>Products</Divider>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={selectedInvoice.products}
                  rowKey={(_, idx) => idx}
                  columns={[
                    { title: "Product", dataIndex: "name", key: "name" },
                    { title: "Qty", dataIndex: "quantity", key: "qty", width: 60 },
                    { title: "Price", dataIndex: "unitPrice", key: "price", width: 80, render: (p) => `$${p.toFixed(2)}` },
                    { title: "Total", dataIndex: "lineTotal", key: "total", width: 90, render: (t) => `$${t.toFixed(2)}` },
                  ]}
                />
              </>
            )}

            <Divider style={{ marginTop: 16, marginBottom: 8 }} />

            {/* Totals */}
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <div style={{ marginBottom: 4 }}>
                <Text>Subtotal: </Text>
                <Text strong>${selectedInvoice.productCost.toFixed(2)}</Text>
              </div>
              {selectedInvoice.discountAmount > 0 && (
                <div style={{ marginBottom: 4, color: "#3f8600" }}>
                  <Text>Discount ({selectedInvoice.discountLabel}): </Text>
                  <Text strong>-${selectedInvoice.discountAmount.toFixed(2)}</Text>
                </div>
              )}
              {selectedInvoice.taxAmount > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <Text>Tax ({accountSettings?.taxRate || 6}%): </Text>
                  <Text strong>${selectedInvoice.taxAmount.toFixed(2)}</Text>
                </div>
              )}
              <div style={{ marginBottom: 4, fontSize: 16 }}>
                <Text strong>Total: </Text>
                <Text strong style={{ color: "#3f8600" }}>${selectedInvoice.totalCost.toFixed(2)}</Text>
              </div>
              <div style={{ marginBottom: 4 }}>
                <Text>Paid: </Text>
                <Text strong style={{ color: "#3f8600" }}>${selectedInvoice.totalPaid.toFixed(2)}</Text>
              </div>
              <div style={{ fontSize: 16 }}>
                <Text strong>Balance Due: </Text>
                <Text strong style={{ color: selectedInvoice.balanceDue > 0 ? "#cf1322" : "#3f8600" }}>
                  ${selectedInvoice.balanceDue.toFixed(2)}
                </Text>
                <Tag
                  color={selectedInvoice.paymentStatus === "PAID" ? "success" : selectedInvoice.paymentStatus === "PARTIAL" ? "warning" : "error"}
                  style={{ marginLeft: 8 }}
                >
                  {selectedInvoice.paymentStatus}
                </Tag>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Receipt Preview Modal */}
      <ReceiptPreview
        open={!!receiptInvoice}
        onClose={() => setReceiptInvoice(null)}
        invoice={receiptInvoice}
        accountSettings={accountSettings}
      />
    </div>
  );
};

export default ContractsPage;
